import React, { useEffect, useMemo, useState } from 'react';
import Layout from 'Layout';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getAllPosts, getCategoryTree, getPostById } from './function/api';
import { findCategoryPathIds, findPostInCategoryTreeBySlug, getLocalizedValue, loadAllHelpPosts, mapCategoryTreeToGroups } from './function/converter';
import { CategoryTreeAPIResponse, HelpNavigationState, Post } from './function/type';
import './styles.scss';

const HelpDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const lang = (i18n.language || 'en') as 'en' | 'vi';
  const navigationState = location.state as HelpNavigationState | null;

  const [posts, setPosts] = useState<Post[]>([]);
  const [categoryTree, setCategoryTree] = useState<CategoryTreeAPIResponse[]>([]);
  const [article, setArticle] = useState<Post | null>(navigationState?.post || null);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const navigateToHelpSlug = (postSlug: string, postId?: number, post?: Post) => {
    const nextSearchParams = new URLSearchParams();

    nextSearchParams.set('slug', postSlug);

    if (postId) {
      nextSearchParams.set('id', String(postId));
    }

    navigate(`/help?${nextSearchParams.toString()}`, {
      replace: true,
      state: {
        selectedCategoryId: navigationState?.selectedCategoryId,
        expandedCategoryIds: navigationState?.expandedCategoryIds,
        ...(post ? { post } : {}),
      } satisfies HelpNavigationState,
    });
  };

  const categories = useMemo(() => mapCategoryTreeToGroups(categoryTree, posts), [categoryTree, posts]);

  const activeCategory = useMemo(() => {
    if (!article) {
      return null;
    }

    return categories.find((item) => item.categoryId === article.category_id) || null;
  }, [article, categories]);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }

    const query = searchQuery.toLowerCase();

    return posts.filter((item) => {
      const title = getLocalizedValue(item.title, lang).toLowerCase();
      const excerpt = getLocalizedValue(item.excerpt, lang).toLowerCase();
      return title.includes(query) || excerpt.includes(query);
    });
  }, [lang, posts, searchQuery]);

  const relatedArticles = useMemo(() => {
    if (!article || !activeCategory) {
      return [];
    }

    return activeCategory.articles.filter((item) => item.id !== article.id);
  }, [activeCategory, article]);

  useEffect(() => {
    let isMounted = true;

    const fetchDetailData = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const [allPosts, allCategories] = await Promise.all([
          loadAllHelpPosts(getAllPosts),
          getCategoryTree(),
        ]);

        if (!isMounted) {
          return;
        }

        setPosts(allPosts);
        setCategoryTree(allCategories);

        const articleId = Number(searchParams.get('id'));
        const slugFromQuery = searchParams.get('slug')?.trim();
        const targetSlug = slug || slugFromQuery;
        let resolvedArticle = navigationState?.post || null;
        const matchedPost = targetSlug ? findPostInCategoryTreeBySlug(allCategories, targetSlug) : null;

        if (resolvedArticle && targetSlug && resolvedArticle.slug !== targetSlug) {
          resolvedArticle = null;
        }

        if (!resolvedArticle && matchedPost) {
          resolvedArticle = await getPostById(matchedPost.id);
        }

        if (!resolvedArticle && Number.isFinite(articleId) && articleId > 0) {
          resolvedArticle = await getPostById(articleId);
        }

        if (!resolvedArticle && targetSlug) {
          resolvedArticle = allPosts.find((item) => item.slug === targetSlug) || null;
        }

        if (!isMounted) {
          return;
        }

        if (resolvedArticle && location.pathname !== '/help') {
          navigateToHelpSlug(targetSlug || resolvedArticle.slug, resolvedArticle.id, resolvedArticle);
          return;
        }

        setArticle(resolvedArticle);

        if (resolvedArticle) {
          const categoryPath = findCategoryPathIds(allCategories, resolvedArticle.category_id);
          setExpandedCategoryIds(categoryPath.length > 0 ? categoryPath : [`cat-${resolvedArticle.category_id}`]);
        } else if (allPosts[0]) {
          const categoryPath = findCategoryPathIds(allCategories, allPosts[0].category_id);
          setExpandedCategoryIds(categoryPath.length > 0 ? categoryPath : [`cat-${allPosts[0].category_id}`]);
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(t('help_load_failed', 'Unable to load help articles right now.'));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchDetailData();

    return () => {
      isMounted = false;
    };
  }, [location.pathname, navigationState?.expandedCategoryIds, navigationState?.post, navigationState?.selectedCategoryId, searchParams, slug, t]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategoryIds((previousIds) =>
      previousIds.includes(categoryId)
        ? previousIds.filter((id) => id !== categoryId)
        : [...previousIds, categoryId]
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsSearching(value.trim().length > 0);
  };

  const goToArticle = async (item: Post) => {
    try {
      const fullPost = await getPostById(item.id);
      navigateToHelpSlug(fullPost.slug, fullPost.id, fullPost);
    } catch (error) {
      navigateToHelpSlug(item.slug, item.id);
    }

    setSearchQuery('');
    setIsSearching(false);
  };

  const goBack = () => {
    navigate('/help', {
      state: {
        selectedCategoryId: navigationState?.selectedCategoryId,
        expandedCategoryIds: navigationState?.expandedCategoryIds,
      } satisfies HelpNavigationState,
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="help-page">
          <div className="help-page__body">
            <div className="help-page__main" style={{ padding: '60px', textAlign: 'center' }}>
              <p>{t('help_loading', 'Loading help articles...')}</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (errorMessage) {
    return (
      <Layout>
        <div className="help-page">
          <div className="help-page__body">
            <div className="help-page__main" style={{ padding: '60px', textAlign: 'center' }}>
              <h2>{errorMessage}</h2>
              <p style={{ marginTop: '12px' }}>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={goBack}
                  onKeyDown={(e) => e.key === 'Enter' && goBack()}
                  style={{ color: '#e8a838', cursor: 'pointer' }}
                >
                  ← {t('help_back_to_help')}
                </span>
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <div className="help-page">
          <div className="help-page__body">
            <div className="help-page__main" style={{ padding: '60px', textAlign: 'center' }}>
              <h2>{t('help_not_found')}</h2>
              <p style={{ marginTop: '12px' }}>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={goBack}
                  onKeyDown={(e) => e.key === 'Enter' && goBack()}
                  style={{ color: '#e8a838', cursor: 'pointer' }}
                >
                  ← {t('help_back_to_help')}
                </span>
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{getLocalizedValue(article.title, lang)} - {t('help_page_title')} - Lumir</title>
        <meta name="description" content={getLocalizedValue(article.excerpt, lang) || getLocalizedValue(article.title, lang)} />
      </Helmet>

      <div className="help-page">
        <div className="help-page__hero">
          <h1 className="help-page__hero-title">{t('help_hero_title')}</h1>
          <form className="help-page__search-form" onSubmit={(e) => e.preventDefault()}>
            <input
              type="text"
              className="help-page__search-input"
              placeholder={t('help_search_placeholder')}
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <button type="submit" className="help-page__search-btn" aria-label="Search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
          </form>
        </div>

        <div className="help-page__body">
          <aside className="help-page__sidebar">
            {categories.map((category) => {
              const isExpanded = expandedCategoryIds.includes(category.id);

              return (
                <div key={category.id} className="help-page__cat">
                  <div
                    className={`help-page__cat-header ${isExpanded ? 'help-page__cat-header--open' : ''}`}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <span className="help-page__cat-title">{getLocalizedValue(category.title, lang)}</span>
                    <svg className={`help-page__cat-arrow ${isExpanded ? 'help-page__cat-arrow--open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </div>

                  {isExpanded && (
                    <ul className="help-page__sub-list">
                      {category.articles.map((item) => (
                        <li key={item.id}>
                          <div
                            className={`help-page__sub-link ${item.id === article.id && !isSearching ? 'help-page__sub-link--active' : ''}`}
                            onClick={() => goToArticle(item)}
                          >
                            {getLocalizedValue(item.title, lang)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </aside>

          <main className="help-page__main">
            {isSearching ? (
              <>
                <h2 className="help-page__section-title">
                  {t('help_search_results')} "{searchQuery}"
                </h2>
                {searchResults.length > 0 ? (
                  <ul className="help-page__article-list">
                    {searchResults.map((item) => (
                      <li key={item.id} className="help-page__article-item" onClick={() => goToArticle(item)}>
                        <span className="help-page__article-title">{getLocalizedValue(item.title, lang)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="help-page__no-results">{t('help_no_results')}</p>
                )}
              </>
            ) : (
              <>
                <div className="help-page__breadcrumb">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={goBack}
                    onKeyDown={(e) => e.key === 'Enter' && goBack()}
                  >
                    {t('help_page_title')}
                  </span>
                  <span className="help-page__breadcrumb-sep">›</span>
                  <span>{activeCategory ? getLocalizedValue(activeCategory.title, lang) : ''}</span>
                </div>

                <div
                  className="help-page__article-detail"
                  dangerouslySetInnerHTML={{
                    __html: getLocalizedValue(article.content, lang),
                  }}
                />

                {relatedArticles.length > 0 && (
                  <div className="help-page__related">
                    <h3 className="help-page__related-title">{t('help_related_articles')}</h3>
                    <ul className="help-page__article-list">
                      {relatedArticles.map((item) => (
                        <li key={item.id} className="help-page__article-item" onClick={() => goToArticle(item)}>
                          <span className="help-page__article-title">{getLocalizedValue(item.title, lang)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </Layout>
  );
};

export default HelpDetail;
